// stores/auth.js
import {defineStore} from 'pinia'

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null,
        userProfile: null,
        isAuthenticated: false,
        loading: false,
        refreshing: false
    }),

    getters: {
        isLoggedIn: (state) => state.isAuthenticated && state.user !== null,
        userName: (state) => state.user ? `${state?.user?.firstName} ${state?.user?.lastName}` : '',
        userEmail: (state) => state.user?.email || '',
        isAdmin: (state) => state.user?.role?.permissions ? Object.keys(state.user?.role?.permissions).some(el => el.toLowerCase().includes('manage_all')):false,
        isManager: (state) => state.user?.role?.name === 'manager',
        userId: (state) => state.user?.id || null
    },

    actions: {
        async login(credentials) {
            this.loading = true
            const { login } = useApi()

            try {
                const response = await login(credentials)

                if (response.status === 'success') {
                    this.user = response.data.user
                    this.isAuthenticated = true
                    await this.getUserProfile(this.user.id)
                    // Démarrer le refresh automatique proactif
                    this.startRefreshTimer()

                    return response
                } else {
                    throw new Error(response.message || 'Login failed')
                }
            } catch (error) {
                //console.error('Login error:', error)
                this.isAuthenticated = false
                this.user = null
                throw error
            } finally {
                this.loading = false

            }
        },

        async logout() {
            this.loading = true
            const { logout } = useApi()

            try {
                // Arrêter le timer de refresh
                this.stopRefreshTimer()

                // Le logout côté serveur supprimera le cookie auth-token
                await logout()
            } catch (error) {
                //console.error('Logout error:', error)
            } finally {
                this.user = null
                this.userProfile = null
                this.isAuthenticated = false
                this.loading = false

                // Nettoyer le cookie côté client aussi (au cas où)
                document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname + ';'
            }
        },

        async checkAuth() {
            // Pas besoin de vérifier un token, juste essayer de récupérer le profil
            // Si le cookie existe et est valide, ça marchera
            const { getProfile } = useApi()

            try {
                const response = await getProfile()

                if (response.status === 'success') {
                    this.user = response.data.user
                    this.isAuthenticated = true

                    // Démarrer le refresh automatique
                    this.startRefreshTimer()

                    return true
                }
            } catch (error) {
                this.user = null
                this.isAuthenticated = false
                throw error
                //console.log('Auth check failed (no valid cookie):', error)
            }

            return false
        },

        async getUserProfile(userId) {
            this.loading = true
            this.error = null

            try {
                const api = useApi()
                const response = await api.apiFetch(`/user/${userId}/profile`, {
                    method: 'GET'
                })

                if (response.success) {
                    this.userProfile = response.data
                }

                return response
            } catch (error) {
                this.handleError(error)
                throw error
            } finally {
                this.loading = false
            }
        },

        async updateUser(userId, userData) {
            this.loading = true
            this.error = null

            try {
                const api = useApi()
                const response = await api.updateUser(userId, userData)

                await this.getUserProfile(this.user.id)

                return response
            } catch (error) {
                this.handleError(error)
                throw error
            } finally {
                this.loading = false
            }
        },

        async refreshTokenProactively() {
            const { refreshToken } = useApi()

            try {
                const response = await refreshToken()
                //console.log('Cookie token refreshed proactively')

                // Redémarrer le timer pour le prochain refresh
                this.startRefreshTimer()

                return response
            } catch (error) {
                //console.error('Proactive refresh failed:', error)
                // Si le refresh échoue, déconnecter l'utilisateur
                await this.logout()
                throw error
            }
        },

        startRefreshTimer() {
            // Arrêter le timer existant s'il y en a un
            this.stopRefreshTimer()

            // Refresh proactif toutes les 50 minutes (si token expire à 60min)
            // Ajustez selon la durée de vie de vos tokens
            this.refreshTimer = setInterval(() => {
                if (this.isAuthenticated) {
                    this.refreshTokenProactively().catch(() => {
                        // Erreur déjà gérée dans refreshTokenProactively
                    })
                }
            }, 50 * 60 * 1000) // 50 minutes

            //console.log('Cookie refresh timer started')
        },

        stopRefreshTimer() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer)
                this.refreshTimer = null
                //console.log('Cookie refresh timer stopped')
            }
        },



        // Inscription
        async register(userData) {
            this.loading = true
            const { register } = useApi()

            try {
                const response = await register(userData)

                if (response.status === 'success') {
                    this.user = response.data.user
                    this.isAuthenticated = true
                    return response
                } else {
                    throw new Error(response.message || 'Registration failed')
                }
            } catch (error) {
                //console.error('Registration error:', error)
                this.isAuthenticated = false
                this.user = null
                throw error
            } finally {
                this.loading = false
            }
        },

        // Déconnexion de tous les appareils
        async logoutAll() {
            const { logoutAll } = useApi()

            try {
                if (this.isAuthenticated) {
                    const response = await logoutAll()
                }
            } catch (error) {
                //console.error('LogoutAll API error:', error)
                throw error
            } finally {
                // Nettoyer les données locales
                this.user = null
                this.isAuthenticated = false

                // Rediriger vers la page de connexion
                await navigateTo('/auth/login')
            }
        },


        // Rafraîchir le token
        async refreshToken() {
            if (this.refreshing) return

            this.refreshing = true
            const { refreshToken } = useApi()

            try {
                const response = await refreshToken()

                if (response.status === 'success') {
                    // Le token est automatiquement mis à jour dans le cookie
                    return response
                } else {
                    throw new Error(response.message || 'Token refresh failed')
                }
            } catch (error) {
                //console.error('Token refresh error:', error)
                await this.logout()
                throw error
            } finally {
                this.refreshing = false
            }
        },

        // Vérifier le token
        async verifyToken() {
            const { verifyToken } = useApi()

            try {
                const response = await verifyToken()

                if (response.status === 'success') {
                    this.user = response.data.user
                    this.isAuthenticated = true
                    return response
                } else {
                    throw new Error(response.message || 'Token verification failed')
                }
            } catch (error) {
                //console.error('Token verification error:', error)
                this.user = null
                this.isAuthenticated = false
                throw error
            }
        },

        // Mot de passe oublié
        async forgotPassword(email) {
            this.loading = true
            const { forgotPassword } = useApi()

            try {
                const response = await forgotPassword(email)

                if (response.status === 'success') {
                    return response
                } else {
                    throw new Error(response.message || 'Forgot password failed')
                }
            } catch (error) {
                //console.error('Forgot password error:', error)
                throw error
            } finally {
                this.loading = false
            }
        },

        // Réinitialiser le mot de passe
        async resetPassword(resetData) {
            this.loading = true
            const { resetPassword } = useApi()

            try {
                const response = await resetPassword(resetData)

                if (response.status === 'success') {
                    return response
                } else {
                    throw new Error(response.message || 'Password reset failed')
                }
            } catch (error) {
                //console.error('Password reset error:', error)
                throw error
            } finally {
                this.loading = false
            }
        },

        // Changer le mot de passe
        async changePassword(userId, passwordData) {
            this.loading = true
            const { changePassword } = useApi()

            try {
                return await changePassword(userId, passwordData)

            } catch (error) {
                //console.error('Password change error:', error)
                throw error
            } finally {
                this.loading = false
            }
        },
        async changePassword_admin(userId, passwordData) {
            this.loading = true
            const { changePassword_admin } = useApi()

            try {
                return await changePassword_admin(userId, passwordData)
            } catch (error) {
                //console.error('Password change error:', error)
                throw error
            } finally {
                this.loading = false
            }
        },

        // Obtenir les sessions actives
        async getSessions() {
            const { getSessions } = useApi()

            try {
                const response = await getSessions()

                if (response.status === 'success') {
                    return response.data.sessions
                } else {
                    throw new Error(response.message || 'Failed to get sessions')
                }
            } catch (error) {
                //console.error('Error getting sessions:', error)
                throw error
            }
        },

        // Supprimer une session
        async deleteSession(sessionId) {
            const { deleteSession } = useApi()

            try {
                const response = await deleteSession(sessionId)

                if (response.status === 'success') {
                    return response
                } else {
                    throw new Error(response.message || 'Failed to delete session')
                }
            } catch (error) {
                //console.error('Error deleting session:', error)
                throw error
            }
        },

        // Vérifier les permissions
        hasPermission(permission) {
            if (!this.user || !this.user.role.permissions) return false
            return this.user.role.permissions[permission]
        },

        // Obtenir les statistiques utilisateur (si disponible dans votre API)
        async getUserStats() {
            const { getUserStats } = useApi()

            try {
                const response = await getUserStats()

                if (response.status === 'success') {
                    return response.data.stats
                } else {
                    throw new Error(response.message || 'Failed to get user stats')
                }
            } catch (error) {
                //console.error('Error getting user stats:', error)
                throw error
            }
        }
    },

    persist: {
        key: 'auth',
        secure: process.env.NODE_ENV === 'production',
        paths: ['user', 'userProfile', 'isAuthenticated','loading','refreshing']
    }
})